import * as R from 'ramda';
import { CSS } from '@dnd-kit/utilities';
import { DragOverlay } from '@dnd-kit/core';
import { shouldUpdate } from 'react-recompose';
import { FixedSizeList as List, areEqual } from 'react-window';
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import React, {
  memo,
  useMemo,
  useState,
  useLayoutEffect,
} from 'react';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
// icons
import * as I from '../../../../svgs';
// ui
import { Box, Flex } from '../../../../ui';
// component edit-report
import { FieldSelect } from '../field-select';
// feature edit-report
import { setOptions, getReportFieldSortableId } from '../../helpers';
//////////////////////////////////////////////////

const FIELD_ITEM_HEIGHT = 52;
const FIELD_LIST_WIDTH = 310;
const FIELD_LIST_MIN_HEIGHT = 120;
const FIELD_LIST_HEIGHT_OFFSET = 225;

const getFieldListHeight = () => Math.max(
  R.subtract(window.innerHeight, FIELD_LIST_HEIGHT_OFFSET), // eslint-disable-line no-undef
  FIELD_LIST_MIN_HEIGHT,
);

export const useFieldListHeight = () => {
  const [listHeight, setListHeight] = useState(getFieldListHeight);

  useLayoutEffect(() => {
    const handleResize = () => setListHeight(getFieldListHeight());

    window.addEventListener('resize', handleResize); // eslint-disable-line no-undef

    return () => window.removeEventListener('resize', handleResize); // eslint-disable-line no-undef
  }, []);

  return listHeight;
};

const ItemAction = memo((props) => {
  const { freezed, fieldIndex, handleRemoveReportField, handleChangeReportFieldFrozen } = props;

  const darkBlueThemeColor = 'colors.dark.blue';

  const pinText = G.ifElse(
    G.isTrue(freezed),
    G.getWindowLocale('titles:unpin', 'Unpin'),
    G.getWindowLocale('titles:pin', 'Pin'),
  );

  const pinColor = G.getThemeByCond(
    G.isTrue(freezed),
    'colors.light.blue',
    'icons.iconColorGrey',
  );

  return (
    <Flex>
      <Box>
        <Flex
          ml={10}
          alignItems='center'
          title={G.getWindowLocale('titles:drag-and-drop', 'Drag And Drop')}
        >
          {I.dnd(G.getTheme(darkBlueThemeColor), 20, 20)}
        </Flex>
      </Box>
      <Box>
        <Flex
          ml={10}
          cursor='pointer'
          alignItems='center'
          onPointerDown={G.stopPropagation}
          onClick={() => handleRemoveReportField(fieldIndex)}
          title={G.getWindowLocale('titles:remove', 'Remove')}
        >
          {I.trash(G.getTheme(darkBlueThemeColor))}
        </Flex>
      </Box>
      <Box>
        <Flex
          ml={10}
          title={pinText}
          cursor='pointer'
          alignItems='center'
          onPointerDown={G.stopPropagation}
          onClick={() => handleChangeReportFieldFrozen({ freezed, fieldIndex })}
        >
          {I.pin(pinColor)}
        </Flex>
      </Box>
    </Flex>
  );
});

const FieldItem = (props) => {
  const {
    name,
    zIndex,
    freezed,
    fieldIndex,
    collection,
    handleRemoveReportField,
    availableForReportsOptions,
    handleSelectReportFieldName,
    availableFieldOptionsByValue,
    handleChangeReportFieldFrozen,
  } = props;

  const field = R.prop(name, availableFieldOptionsByValue);

  const options = useMemo(() => (
    G.ifElse(field, R.concat([field], availableForReportsOptions), availableForReportsOptions)
  ), [field, availableForReportsOptions]);

  return (
    <Flex py={10} alignItems='center' zIndex={zIndex}>
      <ItemAction
        freezed={freezed}
        fieldIndex={fieldIndex}
        handleRemoveReportField={handleRemoveReportField}
        handleChangeReportFieldFrozen={handleChangeReportFieldFrozen}
      />
      <FieldSelect
        value={name}
        options={options}
        selectedOption={field}
        collection={collection}
        fieldIndex={fieldIndex}
        id={`report-field-${fieldIndex}`}
        action={handleSelectReportFieldName}
      />
    </Flex>
  );
};

const FieldRowContent = (props) => {
  const {
    field,
    index,
    maxIndex,
    isDragging,
    itemBorderColor,
    handleRemoveReportField,
    availableForReportsOptions,
    handleSelectReportFieldName,
    availableFieldOptionsByValue,
    handleChangeReportFieldFrozen,
  } = props;

  return (
    <Box
      width='100%'
      alignItems='start'
      border='1px solid'
      borderColor={itemBorderColor}
      opacity={G.ifElse(isDragging, 0.95, 1)}
      zIndex={R.subtract(maxIndex, R.inc(index))}
    >
      <FieldItem
        {...field}
        fieldIndex={index}
        handleRemoveReportField={handleRemoveReportField}
        availableForReportsOptions={availableForReportsOptions}
        handleSelectReportFieldName={handleSelectReportFieldName}
        availableFieldOptionsByValue={availableFieldOptionsByValue}
        handleChangeReportFieldFrozen={handleChangeReportFieldFrozen}
      />
    </Box>
  );
};

const getReportFieldItemKey = (index, data) => {
  const field = R.path(['reportFieldList', index], data);

  if (R.isNil(field)) return `report-field-missing-${index}`;

  return getReportFieldSortableId(field, index);
};

const FieldRow = memo(({ data, index, style }) => {
  const field = R.path(['reportFieldList', index], data);
  const sortableId = R.isNil(field)
    ? `report-field-missing-${index}`
    : getReportFieldSortableId(field, index);

  const {
    listeners,
    transform,
    attributes,
    setNodeRef,
    transition,
  } = useSortable({
    id: sortableId,
    disabled: R.isNil(field),
    animateLayoutChanges: () => false,
  });

  if (R.isNil(field)) return null;

  const isHidden = R.equals(sortableId, data.draggingFieldId);

  const sortableStyle = {
    ...style,
    transition,
    boxSizing: 'border-box',
    opacity: isHidden ? 0.1 : 1,
    cursor: isHidden ? 'grabbing' : 'grab',
    transform: CSS.Translate.toString(transform),
  };

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle}
      {...attributes}
      {...listeners}
    >
      <FieldRowContent
        {...data}
        field={field}
        index={index}
        isDragging={false}
      />
    </div>
  );
}, areEqual);

const enhance = shouldUpdate((props, nextProps) => {
  const toDiff = [
    'listHeight',
    'dndProcessing',
    'draggingFieldId',
    'reportFieldList',
    'availableFields',
    'availableForReports',
  ];

  const shouldUpdate = R.any((field) => (
    G.notEquals(R.prop(field, props), R.prop(field, nextProps))
  ))(toDiff);

  return shouldUpdate;
});

export const Fields = enhance((props) => {
  const {
    listHeight,
    dndProcessing,
    draggingFieldId,
    reportFieldList,
    availableFields,
    availableForReports,
    handleRemoveReportField,
    handleSelectReportFieldName,
    handleChangeReportFieldFrozen,
  } = props;

  const border = G.ifElse(G.isTrue(dndProcessing), '1px dashed grey', 'none');

  const itemBorderColor = G.getTheme('colors.lightGrey');
  const maxIndex = R.multiply(R.length(availableFields), 5);

  const availableForReportsOptions = useMemo(
    () => R.or(setOptions(availableForReports), []),
    [availableForReports],
  );

  const availableFieldOptionsByValue = useMemo(
    () => R.indexBy(R.prop(GC.FIELD_VALUE), R.or(setOptions(availableFields), [])),
    [availableFields],
  );

  const reportFieldListToUse = useMemo(() => R.map(
    (field) => {
      const { name, origValue, referenceFieldName } = field;

      if (R.or(R.isNil(referenceFieldName), G.isNotNilAndNotEmpty(origValue))) return field;

      return R.assoc(
        GC.FIELD_NAME,
        `Ref (${G.getWindowLocale(...GC.referenceFieldNameLocaleMap[referenceFieldName])}): ${name}`,
        field,
      );
    },
    reportFieldList,
  ), [reportFieldList]);

  const sortableIds = useMemo(
    () => R.addIndex(R.map)(
      (field, index) => getReportFieldSortableId(field, index),
      reportFieldListToUse,
    ),
    [reportFieldListToUse],
  );

  const draggingIndex = R.indexOf(draggingFieldId, sortableIds);

  const draggingField = R.gte(draggingIndex, 0)
    ? R.path([draggingIndex], reportFieldListToUse)
    : null;

  const itemData = useMemo(() => ({
    maxIndex,
    draggingFieldId,
    itemBorderColor,
    handleRemoveReportField,
    availableForReportsOptions,
    handleSelectReportFieldName,
    availableFieldOptionsByValue,
    handleChangeReportFieldFrozen,
    reportFieldList: reportFieldListToUse,
  }), [
    maxIndex,
    draggingFieldId,
    itemBorderColor,
    reportFieldListToUse,
    handleRemoveReportField,
    availableForReportsOptions,
    handleSelectReportFieldName,
    availableFieldOptionsByValue,
    handleChangeReportFieldFrozen,
  ]);

  return (
    <Flex border={border} flexDirection='column'>
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <List
          overscanCount={5}
          height={listHeight}
          itemData={itemData}
          width={FIELD_LIST_WIDTH}
          itemSize={FIELD_ITEM_HEIGHT}
          itemKey={getReportFieldItemKey}
          itemCount={R.length(reportFieldListToUse)}
        >
          {FieldRow}
        </List>
      </SortableContext>
      <DragOverlay>
        {
          draggingField ? (
            <FieldRowContent
              isDragging={true}
              maxIndex={maxIndex}
              field={draggingField}
              index={draggingIndex}
              itemBorderColor={itemBorderColor}
              handleRemoveReportField={handleRemoveReportField}
              availableForReportsOptions={availableForReportsOptions}
              handleSelectReportFieldName={handleSelectReportFieldName}
              availableFieldOptionsByValue={availableFieldOptionsByValue}
              handleChangeReportFieldFrozen={handleChangeReportFieldFrozen}
            />
          ) : null
        }
      </DragOverlay>
    </Flex>
  );
});
